import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useEffect, useCallback, useRef } from 'react';

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } }
};

const staggerChildren = {
  visible: {
    transition: { staggerChildren: 0.2, delayChildren: 0.1 }
  }
};

export default function Contact() {
  const calendarRef = useRef(null);

  // 🔄 Simple Script Load for Inline Widget (Zero Polling, Auto-Embed)
  useEffect(() => {
    const src = 'https://assets.calendly.com/assets/external/widget.js';
    if (document.querySelector(`script[src="${src}"]`)) {
      console.log('📄 Calendly script exists → SKIP');
      return;
    }

    console.log('📥 Loading Calendly script for inline embed...');
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => console.log('✅ INLINE SCRIPT LOADED → Auto-embeds detected divs');
    script.onerror = (err) => console.error('❌ SCRIPT LOAD FAILED:', err);
    document.head.appendChild(script);

    return () => {
      const el = document.querySelector(`script[src="${src}"]`);
      el?.parentNode?.removeChild(el);
    };
  }, []);

  // 🔄 Smooth Scroll to Inline Calendar
  const scrollToCalendar = useCallback(() => {
    calendarRef.current?.scrollIntoView({ 
      behavior: 'smooth', 
      block: 'start' 
    });
  }, []);

  const calendlyUrl = 'https://calendly.com/ourfinancialsuccess111/30min?background_color=f8fafc&text_color=1e293b&primary_color=10b981';

  return (
    <>
      <Helmet>
        <title>Contact Jyotsna | Personalized Financial Guidance for Canadians</title>
        <meta name="description" content="Schedule a free chat with Jyotsna, your licensed Canadian financial advisor. Personalized guidance for retirement, debt, business funding & more." />
        {/* 🔄 CSP Optimized for Inline Calendly Embed (iframes + scripts) */}
        <meta http-equiv="Content-Security-Policy" 
          content="default-src 'self'; 
                   script-src 'self' 'unsafe-inline' 'unsafe-eval' https: https://assets.calendly.com https://calendly.com https://t.calendly.com; 
                   frame-src https://calendly.com https://*.calendly.com; 
                   child-src https://calendly.com https://*.calendly.com https://calendly.com; 
                   connect-src 'self' https://calendly.com https://*.calendly.com https://t.calendly.com wss://*.calendly.com; 
                   style-src 'self' 'unsafe-inline' https://assets.calendly.com https://calendly.com;"
        />
      </Helmet>
      
      {/* Gradient Hero Overlay BG */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-emerald-400 via-emerald-300 to-amber-400 opacity-20 h-screen" />
      
      <div className="relative max-w-6xl mx-auto py-24 md:py-32 px-6 lg:px-8">
        {/* Prominent Advisor Photo Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="mb-24 lg:mb-32 grid md:grid-cols-2 gap-12 lg:gap-16 items-center"
          variants={staggerChildren}
        >
          <motion.div
            variants={staggerChildren}
            className="order-2 md:order-1"
          >
            <div className="relative mx-auto w-80 h-80 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] rounded-3xl shadow-2xl overflow-hidden group hover:shadow-emerald/50 hover:shadow-3xl transition-all duration-700 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-8 md:p-10 border-4 border-white/60 backdrop-blur-sm">
              <img 
                src="/jyotsna-photo.jpg"
                alt="Jyotsna, Licensed Financial Advisor in Canada" 
                className="w-full h-full object-cover rounded-2xl group-hover:scale-105 transition-transform duration-700"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/20 via-emerald-400/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 flex items-end p-8 md:p-10">
                <motion.span 
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  className="text-white font-bold bg-black/70 px-6 py-3 rounded-2xl text-base md:text-lg backdrop-blur-md shadow-lg border border-white/30"
                >
                  Licensed Canadian Expert
                </motion.span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="order-1 md:order-2 text-center md:text-left space-y-8 lg:space-y-10"
            variants={fadeInUp}
          >
            <motion.div variants={fadeInUp}>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-500 bg-clip-text text-transparent leading-tight">
                Meet Jyotsna
              </h2>
              <p className="text-sm md:text-base font-semibold text-emerald-600 mt-4 tracking-wide">Your Licensed Financial Advisor</p>
            </motion.div>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl lg:text-2xl text-gray-700 leading-relaxed max-w-lg mx-auto md:mx-0 font-medium"
            >
              Hi, I'm Jyotsna—here to guide you with proven, government-licensed strategies. Let's start with a free, no-obligation chat.
            </motion.p>
            <motion.div 
              variants={staggerChildren}
              className="flex flex-wrap gap-4 justify-center md:justify-start"
            >
              <Link to="/services" className="btn-primary px-10 py-5 lg:px-12 lg:py-6 text-lg lg:text-xl shadow-2xl hover:shadow-emerald/50 hover:shadow-3xl transform hover:-translate-y-1 transition-all duration-300">
                Explore Services
              </Link>
              {/* 🔄 INLINE CALENDLY: Smooth Scroll Button (100% Reliable, No Popups) */}
              <motion.button 
                onClick={scrollToCalendar}
                className="btn-nav px-10 py-5 lg:px-12 lg:py-6 text-lg lg:text-xl font-bold text-emerald-700 bg-white/90 hover:bg-emerald-50 border-2 border-emerald-400 hover:border-emerald-500 shadow-xl hover:shadow-2xl hover:shadow-emerald/30 transition-all duration-300 rounded-2xl"
                whileHover={{ scale: 1.02, y: -3 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Scroll to inline Calendly calendar for free chat booking"
                title="Fancy inline calendar (mobile-friendly, no popups)"
              >
                <span className="flex items-center justify-center gap-2">
                  📅 Schedule Free Chat
                </span>
              </motion.button>
            </motion.div>
          </motion.div>
        </motion.section>

        {/* 🔥 Option 2: Fancy Inline Embed Calendar (No Popup Hell, Zero Blockers) */}
        <motion.section
          ref={calendarRef}
          id="schedule"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-24 lg:mb-32 max-w-6xl mx-auto"
          variants={staggerChildren}
        >
          <motion.div 
            variants={fadeInUp} 
            className="text-center mb-16 lg:mb-24 max-w-4xl mx-auto"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-500 bg-clip-text text-transparent leading-tight mb-6"
            >
              Book Your Free 30-Min Chat
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl md:text-2xl text-gray-600 font-medium max-w-2xl mx-auto leading-relaxed"
            >
              Pick a time below. Jyotsna will follow up to confirm and prepare personalized insights for your goals.
            </motion.p>
          </motion.div>
          
          <motion.div 
            variants={fadeInUp}
            className="max-w-5xl mx-auto"
          >
            <div 
              className="calendly-inline-widget rounded-3xl shadow-3xl border border-emerald-100/50 overflow-hidden bg-white/90 backdrop-blur-xl"
              data-url="https://calendly.com/ourfinancialsuccess111/30min?background_color=f0fdf4&text_color=065f46&primary_color=f59e0b"
              style={{ height: '700px', minWidth: '320px', maxWidth: '100%' }}
            />
            {/* 🔧 Fallback Link */}
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              className="text-center mt-8 text-sm text-gray-500 italic"
            >
              Can't see the calendar?{' '}
              <a 
                href={calendlyUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="font-semibold text-emerald-600 hover:text-emerald-700 underline hover:no-underline transition-colors"
              >
                Open in new tab →
              </a>
            </motion.p>
          </motion.div>
        </motion.section>

        {/* Enhanced Form */}
        <motion.section id="form" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="max-w-2xl mx-auto mb-24 lg:mb-32">
          <motion.h3 
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl lg:text-5xl font-black mb-16 text-center text-gray-900 bg-gradient-to-r from-emerald-700 via-emerald-600 to-amber-500 bg-clip-text text-transparent"
          >
            Or Send Jyotsna a Quick Message
          </motion.h3>
          <motion.form 
            action="https://formspree.io/f/xrealzdb"
            method="POST" 
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            className="bg-white/70 backdrop-blur-xl shadow-2xl rounded-3xl p-10 md:p-12 lg:p-16 space-y-6 lg:space-y-8 border border-emerald-100/60 hover:border-emerald-200/80 transition-all duration-500"
          >
            <input 
              type="text" 
              name="name" 
              placeholder="Your Full Name *" 
              className="w-full p-6 lg:p-8 border-2 border-gray-200/60 rounded-2xl focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all duration-400 bg-white/50 hover:bg-white/80 shadow-lg text-lg lg:text-xl placeholder-gray-500 font-medium" 
              required 
              aria-label="Your full name"
            />
            <input 
              type="email" 
              name="email" 
              placeholder="Your Email Address *" 
              className="w-full p-6 lg:p-8 border-2 border-gray-200/60 rounded-2xl focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all duration-400 bg-white/50 hover:bg-white/80 shadow-lg text-lg lg:text-xl placeholder-gray-500 font-medium" 
              required 
              aria-label="Your email address"
            />
            <textarea 
              name="message" 
              rows="6" 
              placeholder="Tell me about your financial goals (e.g., retirement savings, business funding, debt reduction...)" 
              className="w-full p-6 lg:p-8 border-2 border-gray-200/60 rounded-2xl focus:border-emerald-500 focus:outline-none focus:ring-4 focus:ring-emerald-500/20 transition-all duration-400 bg-white/50 hover:bg-white/80 shadow-lg text-lg lg:text-xl resize-vertical placeholder-gray-500 font-medium" 
              required 
              aria-label="Your message"
            />
            <motion.button 
              type="submit" 
              whileHover={{ scale: 1.02, y: -3 }} 
              whileTap={{ scale: 0.98 }}
              className="w-full btn-primary text-xl lg:text-2xl py-6 lg:py-8 shadow-2xl hover:shadow-emerald/50 hover:shadow-4xl font-bold bg-gradient-to-r from-emerald-600 via-emerald-500 to-amber-500 hover:from-emerald-500 hover:to-amber-400 transition-all duration-400 rounded-2xl"
            >
              Send Secure Message →
            </motion.button>
          </motion.form>
        </motion.section>

        {/* Social Connects */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-center"
          variants={fadeInUp}
        >
          <motion.h3 
            initial={{ scale: 0.95, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="text-2xl md:text-3xl lg:text-4xl font-semibold mb-16 text-gray-800 bg-gradient-to-r from-emerald-500 to-amber-400 bg-clip-text text-transparent opacity-90"
          >
            Connect or See What Clients Say
          </motion.h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {/* WhatsApp */}
            <motion.a 
              href="https://wa.me/+1XXXXXXXXXX"  // 🔧 UPDATE YOUR #
              className="group relative p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-emerald-50 to-green-50 border-2 border-emerald-100 shadow-lg hover:shadow-xl hover:border-emerald-300 transition-all duration-500 hover:-translate-y-1 bg-white/50 backdrop-blur-sm flex flex-col items-center space-y-3 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
              aria-label="Chat on WhatsApp"
            >
              <motion.span 
                className="text-3xl lg:text-4xl mb-3 opacity-80 group-hover:opacity-100 transition-opacity duration-400"
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                📱
              </motion.span>
              <div className="text-lg lg:text-xl font-medium text-gray-800 group-hover:text-emerald-600 transition-colors">WhatsApp</div>
              <div className="text-xs lg:text-sm text-gray-500">(Instant Chat)</div>
            </motion.a>
            
            {/* LinkedIn */}
            <motion.a 
              href="https://www.linkedin.com/in/our-financial-success-3b77943b1/"
              className="group relative p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-blue-50 to-blue-50 border-2 border-blue-100 shadow-lg hover:shadow-xl hover:border-emerald-300 transition-all duration-500 hover:-translate-y-1 bg-white/50 backdrop-blur-sm flex flex-col items-center space-y-3 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
              aria-label="Connect on LinkedIn"
            >
              <motion.span 
                className="text-3xl lg:text-4xl mb-3 opacity-80 group-hover:opacity-100 transition-opacity duration-400"
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                💼
              </motion.span>
              <div className="text-lg lg:text-xl font-medium text-gray-800 group-hover:text-emerald-600 transition-colors">LinkedIn</div>
              <div className="text-xs lg:text-sm text-gray-500">(Professional)</div>
            </motion.a>
            
            {/* Facebook */}
            <motion.a 
              href="https://www.facebook.com/profile.php?id=61587903674965"
              className="group relative p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-indigo-50 to-blue-50 border-2 border-indigo-100 shadow-lg hover:shadow-xl hover:border-emerald-300 transition-all duration-500 hover:-translate-y-1 bg-white/50 backdrop-blur-sm flex flex-col items-center space-y-3 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
              aria-label="Message on Facebook"
            >
              <motion.span 
                className="text-3xl lg:text-4xl mb-3 opacity-80 group-hover:opacity-100 transition-opacity duration-400"
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                📘
              </motion.span>
              <div className="text-lg lg:text-xl font-medium text-gray-800 group-hover:text-emerald-600 transition-colors">Facebook</div>
              <div className="text-xs lg:text-sm text-gray-500">(Messages)</div>
            </motion.a>
            
            {/* Google Reviews */}
            <motion.a 
              href="https://g.page/jyotsna-finance-yourcity"  // 🔧 UPDATE
              className="group relative p-8 lg:p-10 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-100 shadow-lg hover:shadow-xl hover:border-emerald-300 transition-all duration-500 hover:-translate-y-1 bg-white/50 backdrop-blur-sm flex flex-col items-center space-y-3 text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.4 }}
              aria-label="See what clients say about Jyotsna on Google Reviews"
            >
              <motion.span 
                className="text-3xl lg:text-4xl mb-3 opacity-80 group-hover:opacity-100 transition-opacity duration-400"
                initial={{ scale: 0.8 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
              >
                ⭐
              </motion.span>
              <div className="text-lg lg:text-xl font-medium text-gray-800 group-hover:text-emerald-600 transition-colors">Client Reviews</div>
              <div className="text-xs lg:text-sm text-gray-500">(What Clients Say)</div>
            </motion.a>
          </div>
        </motion.section>
      </div>
    </>
  );
}